import React, { useMemo } from 'react';
import _ from 'lodash';

import { AutScanDataItem, UTMInspection } from '../../../../utils/models';
import { TABLE_ITEM_COUNT_LIMITS } from '../../../../utils/constants';
import { getChunkedData, getWallThicknessValue } from '../../../../utils/utilities';

import CustomSection from '../../custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import classNames from './style.module.scss';

interface Props {
    scanDataItems: AutScanDataItem[];
    utmDataItems: UTMInspection[];
}

const ScanSummary: React.FC<Props> = ({ scanDataItems, utmDataItems }) => {
    const getMinimumReadingsFromUTMItem = (item: UTMInspection) => {
        return item.items.map((utmItem) => {
            const readingObject: Record<string, string> = _.omit(utmItem, ['description', 'comments', 'tmlLocation', 'nominal', 'size']);
            const readings =  Object.keys(readingObject).map((key => +readingObject[key])).filter(reading => !isNaN(reading))

            return {
                description: utmItem.description,
                minimumReading: Math.min(...readings)
            }
        });
    };

    const summaryData = useMemo(() => {
       const mappedScanData = scanDataItems.map((scanDataItem) => _.pick(scanDataItem, ['zoneName', 'scanLocation', 'minimumThickness']));
       const mappedUtmItems = utmDataItems.flatMap((utmDataItem) => {
            const mappedItems = getMinimumReadingsFromUTMItem(utmDataItem);
            const minimumThickness = Math.min(...mappedItems.map((reading) => reading.minimumReading));
            const minimumThicknessDescription = mappedItems.find((item) => item.minimumReading === minimumThickness)?.description;

            return {
                zoneName: utmDataItem.sectionTitle,
                scanLocation: minimumThicknessDescription,
                minimumThickness,
            };
        });

        return [...mappedScanData, ...mappedUtmItems].map((item, index) => ({ ...item, srNo: index + 1 }));
    }, [scanDataItems, utmDataItems]);

    const columns = [
        {
            path: 'srNo',
            label: 'Sr. No.'
        },
        {
            path: 'zoneName',
            label: 'Zone Name',
        },
        {
            path: 'scanLocation',
            label: 'Scan Location',
        },
        {
            path: 'minimumThickness',
            label: 'Minimum Thickness',
        }
    ];


    return ( 
        <>
            {getChunkedData(summaryData, TABLE_ITEM_COUNT_LIMITS.AUT).map((chunk, index) => (
                <CustomSection key={index} sectionTitle='AUT Scan Summary' sectionSubtitle='Measurement Details'>
                    <div className="p-2">
                        <PDFTablePage data={chunk} columns={columns} />
                    </div>
                </CustomSection>
            ))}
           
            {getChunkedData(scanDataItems, TABLE_ITEM_COUNT_LIMITS.AUT).map((chunk, index) => (
                <CustomSection key={index} sectionTitle='AUT Scan Summary' sectionSubtitle='Scan Details'>
                    <div className="p-2">
                        <div className="table-responsive">
                            <table className={classNames.scanDataSummaryTable}>
                                <thead>
                                    <tr>
                                        <th rowSpan={2}>Zone Number</th>
                                        <th colSpan={2}>Scan Area</th>
                                        <th rowSpan={2}>Nominal Thickness</th>
                                        <th rowSpan={2}>Corrosion Allowance</th>
                                        <th colSpan={4}>Minimum Thickness and Location</th>
                                    </tr>
                                    <tr>
                                        <th>Axial Length</th>
                                        <th>Circ. Length</th>
                                        <th>Min. Thickness (mm)</th>
                                        <th>Wall Thickness Loss (%)</th>
                                        <th>Axial Loc.</th>
                                        <th>Circ. Loc.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chunk.map((scanDataItem) => (
                                        <tr key={scanDataItem.zoneName}>
                                            <td>{scanDataItem.zoneName}</td>
                                            <td>{scanDataItem.axialLength}</td>
                                            <td>{scanDataItem.circLength}</td>
                                            <td>{scanDataItem.nominalThickness}</td>
                                            <td>{scanDataItem.corrosionAllowance}</td>
                                            <td>{scanDataItem.minimumThickness}</td>
                                            <td>{getWallThicknessValue(scanDataItem.nominalThickness, scanDataItem.minimumThickness).toFixed(2)}</td>
                                            <td>{scanDataItem.axialLoc}</td>
                                            <td>{scanDataItem.circLoc}</td>
                                        </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </CustomSection>
            ))}
        </>
     );
}
 
export default ScanSummary;